import { init, getDefaultIntegrations, reactRouterV5BrowserTracingIntegration, replayIntegration } from '@sentry/react';
import { logger, addBreadcrumb } from '@commutifi-fe/logger';
import config from 'config/index';
import pkg from '../package.json';
import history from './browserHistory';
import { AdminRouting, CommonRouting, CommuterRouting, PublicRouting } from './routing';
import { SentryTags, THIRD_PARTY_SCRIPT_DOMAINS, createBeforeSendHook, createBeforeBreadcrumbHook } from 'utils/sentry';

// Auto-reload when new app version is deployed (checks on tab visibility change)
import 'utils/clientVersionChecker';
let sentryInitialized = false;
function iterateRoutes<T extends Record<string, any>, S>(obj: T, callback: (value: T[keyof T]) => S) {
  const returnedValues: NonNullable<S>[] = [];
  Object.keys(obj).forEach(key => {
    const callbackValue = callback(obj[key]);
    if (callbackValue) {
      returnedValues.push(callbackValue);
    }
    if (typeof obj[key] === 'object' && obj[key] !== null) {
      iterateRoutes(obj[key], callback);
    }
  });
  return returnedValues;
}
const initSentry = (options?: {
  enableFullReplay: boolean;
}) => {
  if (!config.enableSentry) {
    logger.captureWarning('Sentry is disabled. Error tracking will not be initialized.');
    return;
  }
  if (!config.sentryDSN) {
    logger.captureWarning('Sentry DSN not configured. Error tracking will not be initialized.');
    return;
  }

  // GDPR Two-Tier Strategy (based on Sentry official docs):
  // - Session Replay with full masking: STRICTLY NECESSARY (no consent needed, no PII captured)
  // - Session Replay with unmasking: ANALYTICS (requires explicit consent)
  // See: https://docs.sentry.io/platforms/javascript/session-replay/privacy/
  const enableFullReplay = options?.enableFullReplay ?? false;

  // Initialize Sentry once - this happens on first call
  if (!sentryInitialized) {
    // Double-check if Sentry replay is already active (in case of hot reload)
    if (typeof window !== 'undefined' && (window as any).__SENTRY__?.hub) {
      sentryInitialized = true;
      return;
    }
    const appRoutes = iterateRoutes({
      ...CommuterRouting,
      ...AdminRouting,
      ...CommonRouting,
      ...PublicRouting
    }, value => value.path ? {
      path: value.path
    } : undefined);
    init({
      dsn: config.sentryDSN,
      // Using a tunnel helps prevent ad blockers
      tunnel: `${config.dashboardApiUrl}/sentry/tunnel`,
      // GDPR-Compliant: Always record replays (with privacy settings based on consent)
      // With default masking, no PII is captured = strictly necessary for debugging
      replaysSessionSampleRate: 1.0,
      replaysOnErrorSampleRate: 1.0,
      // Set service name as a tag for distributed tracing
      initialScope: {
        tags: {
          service: 'app-dashboard',
          [SentryTags.REPLAY_PRIVACY_MODE]: enableFullReplay ? 'full' : 'masked'
        }
      },
      integrations: [...getDefaultIntegrations({}), reactRouterV5BrowserTracingIntegration({
        history,
        routes: appRoutes
      }),
      // Note: We intentionally DON'T use captureConsoleIntegration
      // Best practice: Use addBreadcrumb() instead of console.log in production
      // See: src/utils/sentryLogger.ts for proper logging methods

      // GDPR-Compliant Session Replay (always enabled)
      // Privacy settings adjust based on consent level:
      // - Without consent: Full masking (no PII) = strictly necessary
      // - With consent: Unmasked for better debugging = analytics
      // See: https://docs.sentry.io/platforms/javascript/session-replay/privacy/
      replayIntegration({
        // Privacy settings based on consent level
        // Default (maskAllText: true, blockAllMedia: true) captures no PII
        maskAllText: !enableFullReplay,
        // Mask all text unless consent granted
        maskAllInputs: true,
        // Always mask inputs (passwords, credit cards, etc.)
        blockAllMedia: !enableFullReplay,
        // Block media unless consent granted

        // GDPR: Network capture settings
        // By default, Replay captures basic info (URL, method, status, body size) - this is safe
        // Request/response BODIES require opt-in via networkDetailAllowUrls and can contain PII
        // See: https://docs.sentry.io/security-legal-pii/scrubbing/protecting-user-privacy/
        networkCaptureBodies: enableFullReplay,
        // Only capture bodies with consent
        // Only specify networkDetailAllowUrls WITH consent - this enables body capture
        // Without this, only basic request metadata is captured (no PII)
        ...(enableFullReplay ? {
          networkDetailAllowUrls: [window.location.origin, config.dashboardApiUrl, 'https://maps.googleapis.com', 'https://api.stonly.com'],
          networkRequestHeaders: ['content-type', 'accept'],
          networkResponseHeaders: ['content-type', 'sentry-trace', 'baggage', 'x-commutifi-request-id']
        } : {}),
        // Replay duration limits
        maxReplayDuration: enableFullReplay ? 600000 : 300000,
        // 10 min with consent, 5 min without
        minReplayDuration: 3000 // Upload replays longer than 3 seconds

        // CSS classes for fine-grained control (always available):
        // .sentry-mask - mask specific elements
        // .sentry-unmask - unmask specific elements (only works when maskAllText: false)
        // .sentry-block - block specific elements
        // .sentry-ignore - ignore specific elements from replay
      })],
      // Configure which backend URLs should receive trace propagation headers
      // This enables distributed tracing between frontend and backend
      tracePropagationTargets: ['localhost', /^https?:\/\/localhost/, config.dashboardApiUrl, /^https?:\/\/.*\.commutifi\.com/],
      // Performance monitoring sample rate (0.0 to 1.0)
      // Configurable via SENTRY_TRACES_SAMPLE_RATE env var, defaults to 1.0 (100%)
      tracesSampleRate: config.sentryTracesSampleRate,
      release: `${pkg.name}@${pkg.version}`,
      // Use COMMUTIFI_ENV to match backend environment (debug/local/staging/production)
      // Falls back to NODE_ENV if COMMUTIFI_ENV is not set
      environment: config.commutifiEnv || process.env.NODE_ENV,
      // GDPR: Send more context only with explicit consent
      sendDefaultPii: enableFullReplay,
      // Send cookies/headers only with consent

      beforeSend: createBeforeSendHook(enableFullReplay),
      beforeBreadcrumb: createBeforeBreadcrumbHook(enableFullReplay),
      denyUrls: [
      // Browser extensions
      /extensions\//i, /^chrome(-extension)?:\/\//i,
      // Third-party scripts we don't control
      ...THIRD_PARTY_SCRIPT_DOMAINS.map(domain => new RegExp(domain.replace('.', '\\.'), 'i'))]
    });

    // Note: Replay ID is automatically attached to events via beforeSend hook above
    // No need to set it here - the session ID is created lazily when first event fires

    // Add Sentry context for GDPR compliance tracking
    logger.setContext('gdpr_settings', {
      error_tracking: 'strictly_necessary',
      // Always enabled
      session_replay: 'always_enabled_with_masking',
      // GDPR-compliant with masking
      replay_privacy_mode: enableFullReplay ? 'full' : 'masked',
      consent_type: enableFullReplay ? 'analytics_cookie' : 'strictly_necessary'
    });

    // Add breadcrumb for initialization (won't create event, just adds context)
    addBreadcrumb('Sentry initialized with GDPR-compliant settings', 'debug', {
      errorTracking: 'always_enabled',
      sessionReplay: 'always_enabled',
      replayPrivacyMode: enableFullReplay ? 'full' : 'masked',
      environment: process.env.NODE_ENV
    });
    sentryInitialized = true;
  }
};

/**
 * Setup Google Analytics based on user consent.
 *
 * Note: Sentry is initialized early in index.tsx with privacy settings based on
 * region and prior consent. This function only handles Google Analytics consent.
 * The `enableAnalytics` parameter is named to match the CookieConsent component's
 * existing interface (previously `enableFullReplay`).
 */
export const initialSetup = (options: {
  enableFullReplay: boolean;
} = {
  enableFullReplay: false
}) => {
  // Google Analytics consent - only enable if user accepted analytics
  if (options.enableFullReplay) {
    gtag('config', 'G-D5P4WLSG6J', {
      anonymize_ip: false
    });
    grantGAConsent();
  }
};
const grantGAConsent = () => {
  window.gtag('consent', 'update', {
    ad_storage: 'granted',
    analytics_storage: 'granted'
  });
};

/**
 * Detect if user is in EU region based on timezone.
 * This is the same logic used in LocalConfigProvider.
 */
const isEURegion = (): boolean => {
  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const continent = timezone.split('/')[0];
    return continent === 'Europe';
  } catch {
    // If timezone detection fails, assume EU for safety (GDPR compliance)
    return true;
  }
};

/**
 * Check if user has previously accepted analytics cookies.
 * Reads the vanilla-cookieconsent cookie directly since the library isn't initialized yet.
 */
const hasAcceptedAnalyticsCookies = (): boolean => {
  try {
    const ccCookie = document.cookie.split('; ').find(row => row.startsWith('cc_cookie='));
    if (!ccCookie) return false;
    const cookieValue = decodeURIComponent(ccCookie.split('=')[1]);
    const consent = JSON.parse(cookieValue);
    const categories: string[] = consent?.categories || [];
    return categories.includes('analytics') || categories.includes('all');
  } catch {
    return false;
  }
};

/**
 * Initialize Sentry early, BEFORE React renders.
 *
 * This function should be called in index.tsx to ensure ALL errors have Session Replay
 * attached, including errors that occur during initial render or before CookieConsent mounts.
 *
 * IMPORTANT: Sentry config cannot be updated after init. The replay privacy settings
 * (maskAllText, blockAllMedia, etc.) are "baked in" at initialization time.
 * To enable full replay after user consent, a page reload is required.
 *
 * Privacy settings are determined by:
 * - Non-EU users: Full replay enabled (no GDPR consent required)
 * - EU users with analytics consent: Full replay enabled
 * - EU users without consent: Masked replay (GDPR-compliant)
 */
export const initSentryEarly = () => {
  const inEU = isEURegion();

  // Non-EU: Enable full replay (no consent required)
  // EU with prior analytics consent: Enable full replay
  // EU without consent: Masked mode (safe for GDPR)
  const enableFullReplay = !inEU || hasAcceptedAnalyticsCookies();
  initSentry({
    enableFullReplay
  });
};